import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import SessionsDetailModal from '../Components/SessionsDetailModal';
import ActiveSessionsModal from '../Components/ActiveSessionsModal';
import InactiveUsersSection from '../Components/InactiveUsersSection';

interface OverviewStats {
  period_days: number;
  sessions: {
    total: number;
    logged: number;
    guests: number;
    mobile: number;
    web: number;
    avg_duration_seconds: number;
  };
  users: {
    usuarios: number;
    bandas: number;
    empresas: number;
  };
  conversions: {
    total: number;
    usuarios: number;
    bandas: number;
    empresas: number;
    rate: string;
  };
}

interface TimelineData {
  date: string;
  total_sessions: number;
  total_conversions: number;
}

interface UserTypeData {
  user_type: string;
  sessions: number;
  unique_users: number;
  percentage?: string;
}

interface GeoData {
  state: string;
  city?: string;
  total_sessions: number;
}

interface RealTimeData {
  active_now: {
    total: number;
    logged: number;
    guests: number;
    unique_users: number; // Número real de usuários únicos logados
    mobile: number;
    web: number;
  };
  recent_conversions: any[];
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const API_BASE = '/analytics';

const AnalyticsDashboard: React.FC = () => {
  const [period, setPeriod] = useState<number>(30);
  const [loading, setLoading] = useState<boolean>(true);
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [timeline, setTimeline] = useState<TimelineData[]>([]);
  const [userTypes, setUserTypes] = useState<UserTypeData[]>([]);
  const [geographic, setGeographic] = useState<GeoData[]>([]);
  const [realTime, setRealTime] = useState<RealTimeData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSessionsDetailOpen, setIsSessionsDetailOpen] = useState<boolean>(false);
  const [isActiveSessionsOpen, setIsActiveSessionsOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchRealTime, 30000); // Atualiza real-time a cada 30s
    return () => clearInterval(interval);
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchOverview(),
        fetchTimeline(),
        fetchUserTypes(),
        fetchGeographic(),
        fetchRealTime()
      ]);
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      setError(error.message || 'Erro ao carregar dados do analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await api.get(`${API_BASE}/overview?period=${period}`);
      setOverview(response.data.data); // Acessa response.data.data
    } catch (error) {
      console.error('Erro ao buscar overview:', error);
      throw error;
    }
  };

  const fetchTimeline = async () => {
    try {
      const response = await api.get(`${API_BASE}/timeline?period=${period}`);
      setTimeline(response.data.data || []); // Acessa response.data.data
    } catch (error) {
      console.error('Erro ao buscar timeline:', error);
      throw error;
    }
  };

  const fetchUserTypes = async () => {
    try {
      const response = await api.get(`${API_BASE}/user-types?period=${period}`);
      const data = response.data.data || [];
      // Calcular percentagens
      const total = data.reduce((sum: number, item: any) => sum + parseInt(item.sessions), 0);
      const withPercentage = data.map((item: any) => ({
        ...item,
        percentage: total > 0 ? ((parseInt(item.sessions) / total) * 100).toFixed(1) : '0'
      }));
      setUserTypes(withPercentage);
    } catch (error) {
      console.error('Erro ao buscar user types:', error);
      throw error;
    }
  };

  const fetchGeographic = async () => {
    try {
      const response = await api.get(`${API_BASE}/geographic?period=${period}&limit=10`);
      const data = response.data.data;
      // Mesclar estados e cidades em uma única lista para a tabela
      const combined = [
        ...(data.cities || []),
        ...(data.states || []).filter((s: any) => !data.cities?.find((c: any) => c.state === s.state))
      ];
      setGeographic(combined);
    } catch (error) {
      console.error('Erro ao buscar dados geográficos:', error);
      throw error;
    }
  };

  const fetchRealTime = async () => {
    try {
      const response = await api.get(`${API_BASE}/real-time`);
      setRealTime(response.data.data); // Acessa response.data.data
    } catch (error) {
      console.error('Erro ao buscar dados em tempo real:', error);
      // Não lança erro para não bloquear o carregamento inicial
    }
  };

  if (loading && !overview) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Carregando dados...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <h2>Erro ao carregar Analytics</h2>
          <p>{error}</p>
          <button onClick={fetchAllData} style={styles.retryButton}>Tentar Novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Analytics Dashboard</h1>
        <div style={styles.periodSelector}>
          <button
            style={period === 1 ? styles.periodButtonActive : styles.periodButton}
            onClick={() => setPeriod(1)}
          >
            1 dia
          </button>
          <button
            style={period === 7 ? styles.periodButtonActive : styles.periodButton}
            onClick={() => setPeriod(7)}
          >
            7 dias
          </button>
          <button
            style={period === 30 ? styles.periodButtonActive : styles.periodButton}
            onClick={() => setPeriod(30)}
          >
            30 dias
          </button>
          <button
            style={period === 90 ? styles.periodButtonActive : styles.periodButton}
            onClick={() => setPeriod(90)}
          >
            90 dias
          </button>
        </div>
      </div>

      {/* Real-time Stats */}
      {realTime && realTime.active_now && (
        <div style={styles.realTimeSection}>
          <div style={styles.realTimeCardClickable} onClick={() => setIsActiveSessionsOpen(true)}>
            <span style={styles.realTimeLabel}>🟢 Sessões Ativas</span>
            <span style={styles.realTimeValue}>{realTime.active_now.total}</span>
            <span style={styles.realTimeSubLabel}>(últimos 5 min)</span>
            <span style={styles.clickHintSmall}>Clique para ver detalhes</span>
          </div>
          <div style={styles.realTimeCard}>
            <span style={styles.realTimeLabel}>👤 Usuários Únicos</span>
            <span style={styles.realTimeValue}>{realTime.active_now.unique_users || 0}</span>
            <span style={styles.realTimeSubLabel}>(logados)</span>
          </div>
          <div style={styles.realTimeCard}>
            <span style={styles.realTimeLabel}>👥 Visitantes</span>
            <span style={styles.realTimeValue}>{realTime.active_now.guests}</span>
            <span style={styles.realTimeSubLabel}>(não logados)</span>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      {overview && overview.sessions && overview.conversions && (
        <div style={styles.kpiGrid}>
          <div style={styles.kpiCardClickable} onClick={() => setIsSessionsDetailOpen(true)}>
            <div style={styles.kpiLabel}>Total de Sessões</div>
            <div style={styles.kpiValue}>{(overview.sessions.total || 0).toLocaleString()}</div>
            <div style={styles.clickHint}>Clique para detalhes</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Usuários Logados</div>
            <div style={styles.kpiValue}>{(overview.sessions.logged || 0).toLocaleString()}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Visitantes</div>
            <div style={styles.kpiValue}>{(overview.sessions.guests || 0).toLocaleString()}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Conversões</div>
            <div style={styles.kpiValue}>{(overview.conversions.total || 0).toLocaleString()}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Taxa de Conversão</div>
            <div style={styles.kpiValue}>{overview.conversions.rate || '0.00%'}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Usuários Ativos</div>
            <div style={styles.kpiValue}>{(overview.users.usuarios || 0).toLocaleString()}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Bandas Ativas</div>
            <div style={styles.kpiValue}>{(overview.users.bandas || 0).toLocaleString()}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Empresas Ativas</div>
            <div style={styles.kpiValue}>{(overview.users.empresas || 0).toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Timeline Chart */}
      {timeline && timeline.length > 0 && (
        <div style={styles.chartSection}>
          <h2 style={styles.sectionTitle}>Sessões e Conversões ao Longo do Tempo</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value: any) => format(new Date(value), 'dd/MM', { locale: ptBR })}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(value: any) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })}
              />
              <Legend />
              <Line type="monotone" dataKey="total_sessions" stroke="#8884d8" name="Sessões" strokeWidth={2} />
              <Line type="monotone" dataKey="total_conversions" stroke="#82ca9d" name="Conversões" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={styles.twoColumnGrid}>
        {/* User Types Pie Chart */}
        {userTypes && userTypes.length > 0 && (
          <div style={styles.chartSection}>
            <h2 style={styles.sectionTitle}>Distribuição por Tipo de Usuário</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={userTypes}
                  dataKey="sessions"
                  nameKey="user_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  label={(entry: any) => `${entry.user_type}: ${entry.percentage}%`}
                >
                  {userTypes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div style={styles.legend}>
              {userTypes.map((item, index) => (
                <div key={item.user_type} style={styles.legendItem}>
                  <span style={{ ...styles.legendColor, backgroundColor: COLORS[index % COLORS.length] }}></span>
                  <span>{item.user_type}: {item.sessions} ({item.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Geographic Bar Chart */}
        {geographic && geographic.length > 0 && (
          <div style={styles.chartSection}>
            <h2 style={styles.sectionTitle}>Top 10 Estados</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={geographic.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="state" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="total_sessions" fill="#8884d8" name="Sessões" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Geographic Table */}
      {geographic && geographic.length > 0 && (
        <div style={styles.tableSection}>
          <h2 style={styles.sectionTitle}>Distribuição Geográfica Detalhada</h2>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Cidade</th>
                <th style={styles.th}>Sessões</th>
              </tr>
            </thead>
            <tbody>
              {geographic.map((row, index) => (
                <tr key={index} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                  <td style={styles.td}>{row.state || 'N/A'}</td>
                  <td style={styles.td}>{row.city || 'N/A'}</td>
                  <td style={styles.td}>{row.total_sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Inactive Users Section */}
      <InactiveUsersSection />

      {/* Sessions Detail Modal */}
      <SessionsDetailModal
        isOpen={isSessionsDetailOpen}
        onClose={() => setIsSessionsDetailOpen(false)}
        period={period}
      />

      {/* Active Sessions Modal */}
      <ActiveSessionsModal
        isOpen={isActiveSessionsOpen}
        onClose={() => setIsActiveSessionsOpen(false)}
      />
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  } as React.CSSProperties,
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
  } as React.CSSProperties,
  periodSelector: {
    display: 'flex',
    gap: '10px',
  } as React.CSSProperties,
  periodButton: {
    padding: '10px 20px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    cursor: 'pointer',
    borderRadius: '5px',
    fontSize: '14px',
  } as React.CSSProperties,
  periodButtonActive: {
    padding: '10px 20px',
    border: '1px solid #1976d2',
    backgroundColor: '#1976d2',
    color: 'white',
    cursor: 'pointer',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
  } as React.CSSProperties,
  realTimeSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '15px',
    marginBottom: '20px',
  } as React.CSSProperties,
  realTimeCard: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  } as React.CSSProperties,
  realTimeCardClickable: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
  } as React.CSSProperties,
  realTimeLabel: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px',
  } as React.CSSProperties,
  realTimeValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1976d2',
  } as React.CSSProperties,
  realTimeSubLabel: {
    fontSize: '11px',
    color: '#999',
    marginTop: '5px',
  } as React.CSSProperties,
  clickHintSmall: {
    fontSize: '10px',
    color: '#1976d2',
    marginTop: '8px',
    fontWeight: '500',
  } as React.CSSProperties,
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '15px',
    marginBottom: '30px',
  } as React.CSSProperties,
  kpiCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  } as React.CSSProperties,
  kpiCardClickable: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
  } as React.CSSProperties,
  kpiLabel: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px',
  } as React.CSSProperties,
  kpiValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
  } as React.CSSProperties,
  clickHint: {
    fontSize: '11px',
    color: '#1976d2',
    marginTop: '8px',
    fontWeight: '500',
  } as React.CSSProperties,
  chartSection: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '20px',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px',
  } as React.CSSProperties,
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginBottom: '20px',
  } as React.CSSProperties,
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '15px',
  } as React.CSSProperties,
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  } as React.CSSProperties,
  legendColor: {
    width: '16px',
    height: '16px',
    borderRadius: '3px',
  } as React.CSSProperties,
  tableSection: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflowX: 'auto',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  } as React.CSSProperties,
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #ddd',
    backgroundColor: '#f9f9f9',
    fontWeight: 'bold',
    color: '#333',
  } as React.CSSProperties,
  td: {
    padding: '12px',
    borderBottom: '1px solid #eee',
  } as React.CSSProperties,
  trEven: {
    backgroundColor: '#fafafa',
  } as React.CSSProperties,
  trOdd: {
    backgroundColor: 'white',
  } as React.CSSProperties,
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '24px',
    color: '#666',
  } as React.CSSProperties,
  error: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    color: '#d32f2f',
  } as React.CSSProperties,
  retryButton: {
    marginTop: '20px',
    padding: '10px 20px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
  } as React.CSSProperties,
};

export default AnalyticsDashboard;
